/**
 * Carrier MCP stdio — UI-agent write tools.
 *
 * These cover OCS operations with no REST equivalent (confirmed by NOC/Bridge4IP
 * on 2026-05-13), performed by a hosted browser agent driving the OCS web
 * dashboard. Driving it requires logging into it.
 *
 * On the hosted Worker that login comes from Clerk `privateMetadata`, read
 * through the Clerk Backend API with `CLERK_SECRET_KEY` and an org id. stdio has
 * neither, so ten of the eleven tools below refused unconditionally.
 *
 * THE OPT-IN
 * ----------
 * They now run locally when — and only when — the operator has both set
 * `CARRIER_ALLOW_PORTAL_PASSWORD` and supplied `CARRIER_OCS_PORTAL_USERNAME` and
 * `CARRIER_OCS_PORTAL_PASSWORD`. Neither half alone enables anything. See
 * `portal-credentials.ts` for why the gate is two independent acts rather than
 * one, and why the refusal below is byte-identical across all three incomplete
 * states rather than naming the missing half.
 *
 * When the opt-in is absent these return exactly what they returned before: the
 * shared "this needs the Worker" reply in `ui-agent-stub.ts`, unchanged.
 *
 * WHAT A RUN COSTS AND WHAT BOUNDS IT
 * -----------------------------------
 * The same `RunBudget` that bounds `ui_agent_ask`: a per-process ceiling,
 * default 10, settable with `CARRIER_BROWSER_USE_MAX_RUNS`. It caps runs, not
 * dollars, and it does not survive a respawned CLI. See `ui-agent-runtime.ts`.
 *
 * DISPATCH IS SYNCHRONOUS HERE
 * ----------------------------
 * The Worker hands back a task id and finishes the run in a Durable Object. A
 * CLI has no background, so the call blocks for the length of the run and
 * returns the finished result. The response keys the Worker returns
 * (`steel_task_id`, `steel_session_viewer_url`) are kept: MCP clients and
 * apps/console match on those names and neither is built from this package.
 *
 * THE SCHEDULE TOOLS still cannot work here and are still stubs — a CLI process
 * that exits when the client disconnects has no cron. See `ui-agent-stub.ts`.
 *
 * Gap IDs covered:
 *   G-01  createSteeringList            — create a new steering list
 *   G-02  buildSteeringList             — add/remove operators in a steering list
 *   G-03  setSteeringListOnAccount      — assign steering list at account level
 *   G-05  createAccount                 — create a new reseller sub-account
 *   G-12  destination list CRUD         — create/edit/delete destination lists
 *   G-18  deletePackageTemplate         — delete a package template
 *   G-19  editLocationZone/deleteLocationZone — edit or delete location zones
 *   G-20  requestResellerRelayChange    — draft a parent-reseller request
 *
 * THE ONE EXCEPTION
 * -----------------
 * `ui_request_reseller_relay_change` (G-20) is NOT a browser-agent dispatch. It
 * composes a request for the parent reseller from its arguments and returns it
 * with `sent: false`; it touches no vendor, no store and no portal. It worked
 * over stdio before the opt-in and is unaffected by it — it needs no portal
 * credentials and is never gated on them.
 */

import { z } from "zod";
import type { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import type { ToolContext } from "./tools.js";
import type { ToolScope } from "./types.js";
import { uiAgentStubError, type ToolResult } from "./ui-agent-stub.js";
import {
  PORTAL_PASSWORD_RUN_NOTE,
  readPortalOptIn,
  type PortalOptIn,
} from "./portal-credentials.js";
import {
  SpendLimitError,
  type StdioUiAgentRuntime,
} from "./ui-agent-runtime.js";
import {
  OCS_DASHBOARD_DEFAULT,
  assembleUiAgentPrompt,
  browserUseConfigured,
  buildBuildSteeringListPrompt,
  buildCreateAccountPrompt,
  buildCreateDestinationListPrompt,
  buildCreateSteeringListPrompt,
  buildDeleteDestinationListPrompt,
  buildDeleteLocationZonePrompt,
  buildDeletePackageTemplatePrompt,
  buildEditDestinationListPrompt,
  buildEditLocationZonePrompt,
  buildSetAccountSteeringListPrompt,
  redactCredentials,
  runUiAgent,
  type BuildSteeringListParams,
  type CreateAccountParams,
  type CreateDestinationListParams,
  type CreateSteeringListParams,
  type DeleteDestinationListParams,
  type DeleteLocationZoneParams,
  type DeletePackageTemplateParams,
  type EditDestinationListParams,
  type EditLocationZoneParams,
  type SetAccountSteeringListParams,
} from "@carrier/ai";

/**
 * Doc + registry merge, read by `tool-registry.ts`.
 *
 * These describe the tool's contract across both surfaces and the Worker
 * enforces exactly these scopes, so they hold whether the local handler
 * dispatches or refuses.
 */
export const UI_AGENT_TOOL_SCOPES: Record<string, ToolScope> = {
  ui_create_steering_list: "write",
  ui_build_steering_list: "write",
  ui_set_account_steering_list: "write",
  ui_request_reseller_relay_change: "write",
  ui_create_account: "admin",
  ui_create_destination_list: "write",
  ui_edit_destination_list: "write",
  ui_delete_destination_list: "admin",
  ui_delete_package_template: "admin",
  ui_edit_location_zone: "write",
  ui_delete_location_zone: "admin",
};

export const UI_AGENT_DESTRUCTIVE_TOOLS = new Set([
  "ui_create_steering_list",
  "ui_build_steering_list",
  "ui_set_account_steering_list",
  "ui_create_account",
  "ui_create_destination_list",
  "ui_edit_destination_list",
  "ui_delete_destination_list",
  "ui_delete_package_template",
  "ui_edit_location_zone",
  "ui_delete_location_zone",
]);

/**
 * Appended to each gated tool's description.
 *
 * Two variants, because a description that says "requires the remote Worker"
 * while the tool is in fact live locally is the sort of stale claim this
 * codebase keeps tripping over. The opt-in is read once at registration, which
 * is when the process reads its environment anyway.
 */
const STDIO_NOTE_DISABLED =
  " Requires the remote Carrier MCP Worker deployment — the local CLI holds no OCS portal credentials.";
const STDIO_NOTE_ENABLED =
  " Runs locally: CARRIER_ALLOW_PORTAL_PASSWORD is set and OCS portal credentials are supplied, so this drives the dashboard from this machine. Each run costs roughly $0.20-1.00 against your own BROWSER_USE_API_KEY and counts against CARRIER_BROWSER_USE_MAX_RUNS.";

function stdioNote(optIn: PortalOptIn): string {
  return optIn.enabled ? STDIO_NOTE_ENABLED : STDIO_NOTE_DISABLED;
}

/**
 * The refusal, unchanged from before the opt-in and identical for every
 * incomplete state. See `portal-credentials.ts` on why it names no gap.
 */
function portalStub(toolName: string): ToolResult {
  return uiAgentStubError(toolName, "ocs_portal_credentials");
}

function jsonResult(body: unknown, isError = false): ToolResult {
  return {
    ...(isError ? { isError: true } : {}),
    content: [{ type: "text" as const, text: JSON.stringify(body, null, 2) }],
  };
}

/**
 * Scope check, opt-in gate, budget reserve, dispatch.
 *
 * Order matters. Scope is refused first so a token without `admin` gets the
 * scope error it would get on the Worker rather than a stub that implies the
 * tool does not exist here. The opt-in gate comes next, before anything is
 * reserved or spent.
 *
 * NOTHING BELOW PUTS THE PASSWORD ANYWHERE READABLE. The audit row carries the
 * tool name, gap id, status and task id only — never args, never the prompt.
 * The dry-run preview is redacted before it is returned. The `redact` hook on
 * the dispatch covers the hosted agent quoting its own instructions back into
 * the task record, which is exactly how #783 leaked one into KV.
 */
function wrapPortalHandler(
  toolName: string,
  gapId: string,
  requiredScope: ToolScope,
  ctx: ToolContext,
  runtime: StdioUiAgentRuntime,
  optIn: PortalOptIn,
  buildPrompt: (args: Record<string, unknown>, dashboardUrl: string) => string,
) {
  return async (
    args: Record<string, unknown> & { dry_run?: boolean },
  ): Promise<ToolResult> => {
    const start = Date.now();
    const ocsMethod = `[ui-agent:${gapId}]`;

    if (!ctx.props.scope.includes(requiredScope)) {
      ctx.audit({
        tool_name: toolName,
        ocs_method: ocsMethod,
        status: "scope_denied",
        dry_run: false,
        duration_ms: 0,
      });
      return {
        isError: true,
        content: [
          {
            type: "text" as const,
            text: `Scope denied: tool '${toolName}' requires '${requiredScope}' scope. Your token has: [${ctx.props.scope.join(", ")}].`,
          },
        ],
      };
    }

    if (!optIn.enabled) return portalStub(toolName);

    if (!browserUseConfigured(runtime.env)) {
      return jsonResult(
        {
          error: "browser_use_not_configured",
          tool: toolName,
          message:
            "BROWSER_USE_API_KEY is not set, so the dashboard agent cannot run. " +
            "Set it in the environment of the process running this MCP server. " +
            "Runs are billed to that key at roughly $0.20-1.00 each.",
        },
        true,
      );
    }

    const { credentials } = optIn;
    const dashboardUrl = optIn.dashboardUrl ?? OCS_DASHBOARD_DEFAULT;
    const redact = (text: string): string =>
      redactCredentials(text, credentials);
    const fullPrompt = assembleUiAgentPrompt(
      buildPrompt(args, dashboardUrl),
      credentials,
      dashboardUrl,
    );

    // Checked before the budget is consumed: a dry run dispatches nothing and
    // is billed nothing, so it must not burn one of the session's allowance.
    if (args.dry_run === true) {
      ctx.audit({
        tool_name: toolName,
        ocs_method: ocsMethod,
        status: "dry_run",
        dry_run: true,
        duration_ms: 0,
        event_type: "ui_agent_dispatch",
      });
      return jsonResult({
        dry_run: true,
        tool: toolName,
        gap_id: gapId,
        agent_prompt_preview: redact(fullPrompt),
        runs_remaining_this_session: runtime.budget.remaining,
        note: "No browser agent was dispatched. Set dry_run=false to execute.",
        portal_password_note: PORTAL_PASSWORD_RUN_NOTE,
      });
    }

    try {
      runtime.budget.reserve();
    } catch (err) {
      if (err instanceof SpendLimitError) {
        ctx.audit({
          tool_name: toolName,
          ocs_method: ocsMethod,
          // The existing quota status, not a new one: AuditRow's status is a
          // closed union shared with the Worker.
          status: "quota_exceeded",
          dry_run: false,
          duration_ms: 0,
          event_type: "ui_agent_dispatch",
        });
        return jsonResult(
          { error: "run_limit_reached", message: err.message },
          true,
        );
      }
      throw err;
    }

    try {
      const result = await runUiAgent(
        fullPrompt,
        `Carrier CLI UI Agent: ${toolName} (${gapId})`,
        runtime.env,
        { ownerSub: ctx.props.sub, redact },
      );

      if (!result.task_id) {
        ctx.audit({
          tool_name: toolName,
          ocs_method: ocsMethod,
          status: "error",
          dry_run: false,
          duration_ms: Date.now() - start,
          event_type: "ui_agent_dispatch",
        });
        return jsonResult(
          {
            error: "steel_dispatch_failed",
            message: "Failed to create a browser agent task",
          },
          true,
        );
      }

      runtime.dispatchedTaskIds.add(result.task_id);

      ctx.audit({
        tool_name: toolName,
        ocs_method: ocsMethod,
        status: result.status === "error" ? "error" : "ui_agent_dispatched",
        dry_run: false,
        duration_ms: Date.now() - start,
        event_type: "ui_agent_dispatch",
        manus_task_id: result.task_id, // AE blob position is the contract; the name is history
      });

      return jsonResult({
        status:
          result.status === "stopped"
            ? "ui_agent_completed"
            : result.status === "waiting"
              ? "ui_agent_awaiting_input"
              : result.status,
        tool: toolName,
        gap_id: gapId,
        // Held still across the vendor swap and across transports: MCP clients
        // and the console read these names.
        steel_task_id: result.task_id,
        steel_session_viewer_url: result.session_viewer_url,
        // Redacted defensively. `runUiAgent` already applies the same redactor
        // before the record is stored, so this is the second of two passes over
        // the fields an agent can echo its instructions into. Field-by-field
        // rather than over the serialised JSON: a password containing a quote
        // or a backslash is escaped by JSON.stringify and would no longer match
        // a plain substring replace.
        result: result.result
          ? {
              ...result.result,
              summary: redact(result.result.summary),
              ...(result.result.entity_id !== undefined
                ? { entity_id: redact(result.result.entity_id) }
                : {}),
              ...(result.result.error_message !== undefined
                ? { error_message: redact(result.result.error_message) }
                : {}),
            }
          : undefined,
        cost_usd: result.spend?.costUsd,
        session_spend_usd: runtime.spend.costUsd,
        runs_remaining_this_session: runtime.budget.remaining,
        note:
          "The run completed in-process — a local CLI has no background runtime, " +
          "so this call blocked for its duration.",
        portal_password_note: PORTAL_PASSWORD_RUN_NOTE,
      });
    } catch (err) {
      ctx.audit({
        tool_name: toolName,
        ocs_method: ocsMethod,
        status: "error",
        dry_run: false,
        duration_ms: Date.now() - start,
        event_type: "ui_agent_dispatch",
      });
      // Redacted: an HTTP client that echoes the request body into its error
      // message would otherwise put the password in the tool result.
      return {
        isError: true,
        content: [
          {
            type: "text" as const,
            text: redact(
              `Error dispatching the UI agent: ${err instanceof Error ? err.message : String(err)}`,
            ),
          },
        ],
      };
    }
  };
}

export function registerAllUiAgentTools(
  server: McpServer,
  ctx: ToolContext,
  runtime: StdioUiAgentRuntime,
  optIn: PortalOptIn = readPortalOptIn(),
): void {
  const NOTE = stdioNote(optIn);
  const gated = (
    toolName: string,
    gapId: string,
    scope: ToolScope,
    buildPrompt: (
      args: Record<string, unknown>,
      dashboardUrl: string,
    ) => string,
  ) => wrapPortalHandler(toolName, gapId, scope, ctx, runtime, optIn, buildPrompt);

  // =========================================================================
  // G-01: ui_create_steering_list
  // =========================================================================
  server.registerTool(
    "ui_create_steering_list",
    {
      title: "Create Steering List (UI Agent)",
      description:
        "Creates a new network steering list (OPLMN preference configuration) via the OCS web dashboard. " +
        "This operation is not available via the OCS REST API. A browser agent is dispatched " +
        "to perform the operation. Params: `name` (steering list name), `description` (optional)." +
        NOTE,
      inputSchema: {
        name: z.string().describe("Name for the new steering list"),
        description: z
          .string()
          .optional()
          .describe("Optional description for the steering list"),
        dry_run: z
          .boolean()
          .optional()
          .describe("Preview the agent prompt without dispatching"),
      },
    },
    gated("ui_create_steering_list", "G-01", "write", (args, dashboardUrl) =>
      buildCreateSteeringListPrompt(
        args as unknown as CreateSteeringListParams,
        dashboardUrl,
      ),
    ),
  );

  // =========================================================================
  // G-02: ui_build_steering_list
  // =========================================================================
  server.registerTool(
    "ui_build_steering_list",
    {
      title: "Build Steering List (UI Agent)",
      description:
        "Adds or removes operators (MCC-MNC) from an existing steering list via the OCS web dashboard. " +
        "This operation is not available via the OCS REST API. Params: `steering_list_id`, " +
        "`add_operators` (array of MCC-MNC to add), `remove_operators` (array to remove), " +
        "`operator_type` ('priority' or 'excluded')." +
        NOTE,
      inputSchema: {
        steering_list_id: z
          .number()
          .describe("ID of the steering list to modify"),
        add_operators: z
          .array(z.string())
          .optional()
          .describe("MCC-MNC codes to add (e.g. ['20801', '26201'])"),
        remove_operators: z
          .array(z.string())
          .optional()
          .describe("MCC-MNC codes to remove"),
        operator_type: z
          .enum(["priority", "excluded"])
          .default("priority")
          .describe("Whether operators are priority or excluded"),
        dry_run: z
          .boolean()
          .optional()
          .describe("Preview the agent prompt without dispatching"),
      },
    },
    gated("ui_build_steering_list", "G-02", "write", (args, dashboardUrl) =>
      buildBuildSteeringListPrompt(
        args as unknown as BuildSteeringListParams,
        dashboardUrl,
      ),
    ),
  );

  // =========================================================================
  // G-03: ui_set_account_steering_list
  // =========================================================================
  server.registerTool(
    "ui_set_account_steering_list",
    {
      title: "Set Account Steering List (UI Agent)",
      description:
        "Assigns or removes a steering list at the account level via the OCS web dashboard. " +
        "The subscriber-level counterpart `modify_subscriber_steering_list` is available via API; " +
        "this account-level operation is UI-only. Params: `account_id`, `steering_list_id` (0 to remove)." +
        NOTE,
      inputSchema: {
        account_id: z
          .number()
          .describe("Account ID to assign the steering list to"),
        steering_list_id: z
          .number()
          .describe("Steering list ID to assign (0 to remove/unset)"),
        dry_run: z
          .boolean()
          .optional()
          .describe("Preview the agent prompt without dispatching"),
      },
    },
    gated(
      "ui_set_account_steering_list",
      "G-03",
      "write",
      (args, dashboardUrl) =>
        buildSetAccountSteeringListPrompt(
          args as unknown as SetAccountSteeringListParams,
          dashboardUrl,
        ),
    ),
  );

  // =========================================================================
  // G-20: ui_request_reseller_relay_change — never gated, see file header.
  // =========================================================================
  server.registerTool(
    "ui_request_reseller_relay_change",
    {
      title: "Request Reseller Relay Change",
      description:
        "Drafts a parent-reseller/support request for Relay Gy, calls/SMS relay, or VoIP relay when those portal controls are read-only at the current reseller tier. Does not send the request or mutate the portal.",
      inputSchema: {
        reseller_id: z
          .number()
          .optional()
          .describe("Reseller id; defaults to the authenticated reseller"),
        relay_gy: z.boolean().optional().describe("Desired Relay Gy state"),
        relay_calls_sms: z
          .boolean()
          .optional()
          .describe("Desired calls/SMS relay state"),
        relay_voip: z.boolean().optional().describe("Desired VoIP relay state"),
        reason: z
          .string()
          .optional()
          .describe("Operational reason for the requested change"),
      },
    },
    async (args): Promise<ToolResult> => {
      if (!ctx.props.scope.includes("write")) {
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: "Scope denied: tool 'ui_request_reseller_relay_change' requires 'write' scope.",
            },
          ],
        };
      }
      const resellerId = args.reseller_id ?? ctx.props.reseller_id;

      // ctx.props.reseller_id is typed as required but Clerk claims omit it
      // when the session has no reseller. Interpolating that into the draft
      // produced "for reseller undefined" while still reporting success.
      // This tool's output is copy a human sends to the parent reseller, so
      // a plausible-looking draft with a hole in it is worse than an error.
      if (typeof resellerId !== "number" || !Number.isFinite(resellerId)) {
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: JSON.stringify({
                error: "reseller_id_unresolved",
                message:
                  "Could not resolve a reseller id from the session. Pass reseller_id explicitly.",
              }),
            },
          ],
        };
      }

      const requested = [
        args.relay_gy === undefined
          ? null
          : `Relay Gy: ${args.relay_gy ? "enable" : "disable"}`,
        args.relay_calls_sms === undefined
          ? null
          : `Calls/SMS relay: ${args.relay_calls_sms ? "enable" : "disable"}`,
        args.relay_voip === undefined
          ? null
          : `VoIP relay: ${args.relay_voip ? "enable" : "disable"}`,
      ].filter((value): value is string => value !== null);
      if (requested.length === 0) {
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: JSON.stringify({
                error: "relay_state_required",
                message: "Choose at least one relay flag and desired state.",
              }),
            },
          ],
        };
      }
      const draft = [
        `Please update the parent-controlled OCS relay settings for reseller ${resellerId}.`,
        "",
        ...requested.map((line) => `- ${line}`),
        ...(args.reason ? ["", `Reason: ${args.reason}`] : []),
        "",
        "Please confirm once the change is active so we can verify the dependent OCS operation.",
      ].join("\n");
      ctx.audit({
        tool_name: "ui_request_reseller_relay_change",
        ocs_method: "[ui-agent:G-20]",
        status: "parent_request_drafted",
        dry_run: true,
        duration_ms: 0,
        event_type: "ui_agent_dispatch",
      });
      return {
        content: [
          {
            type: "text",
            text: JSON.stringify(
              {
                status: "parent_request_drafted",
                capability: "parent_controlled",
                reseller_id: resellerId,
                parent_reseller:
                  resellerId === 1170 ? "Bridge4IP" : "parent reseller/support",
                draft,
                sent: false,
              },
              null,
              2,
            ),
          },
        ],
      };
    },
  );

  // =========================================================================
  // G-05: ui_create_account
  // =========================================================================
  server.registerTool(
    "ui_create_account",
    {
      title: "Create Account (UI Agent)",
      description:
        "Creates a new sub-account under the reseller via the OCS web dashboard. " +
        "This operation is not available via the OCS REST API. Params: `name` (account name), " +
        "`description` (optional), `initial_balance` (optional, default 0)." +
        NOTE,
      inputSchema: {
        name: z.string().describe("Name for the new account"),
        description: z.string().optional().describe("Optional description"),
        initial_balance: z
          .number()
          .optional()
          .describe("Initial balance in account currency (default 0)"),
        dry_run: z
          .boolean()
          .optional()
          .describe("Preview the agent prompt without dispatching"),
      },
    },
    gated("ui_create_account", "G-05", "admin", (args, dashboardUrl) =>
      buildCreateAccountPrompt(
        args as unknown as CreateAccountParams,
        dashboardUrl,
      ),
    ),
  );

  // =========================================================================
  // G-12: ui_create_destination_list
  // =========================================================================
  server.registerTool(
    "ui_create_destination_list",
    {
      title: "Create Destination List (UI Agent)",
      description:
        "Creates a new destination list (named set of phone number prefixes for MOC call permissions) " +
        "via the OCS web dashboard. Params: `name`, `prefixes` (array of prefix strings), `description`." +
        NOTE,
      inputSchema: {
        name: z.string().describe("Name for the new destination list"),
        prefixes: z
          .array(z.string())
          .optional()
          .describe("Phone number prefixes to include (e.g. ['+31', '+49'])"),
        description: z.string().optional().describe("Optional description"),
        dry_run: z
          .boolean()
          .optional()
          .describe("Preview the agent prompt without dispatching"),
      },
    },
    gated("ui_create_destination_list", "G-12", "write", (args, dashboardUrl) =>
      buildCreateDestinationListPrompt(
        args as unknown as CreateDestinationListParams,
        dashboardUrl,
      ),
    ),
  );

  // =========================================================================
  // G-12: ui_edit_destination_list
  // =========================================================================
  server.registerTool(
    "ui_edit_destination_list",
    {
      title: "Edit Destination List (UI Agent)",
      description:
        "Edits an existing destination list via the OCS web dashboard. " +
        "Params: `destination_list_id`, `add_prefixes`, `remove_prefixes`, `new_name`." +
        NOTE,
      inputSchema: {
        destination_list_id: z
          .number()
          .describe("ID of the destination list to edit"),
        add_prefixes: z
          .array(z.string())
          .optional()
          .describe("Prefixes to add"),
        remove_prefixes: z
          .array(z.string())
          .optional()
          .describe("Prefixes to remove"),
        new_name: z
          .string()
          .optional()
          .describe("Rename the destination list"),
        dry_run: z
          .boolean()
          .optional()
          .describe("Preview the agent prompt without dispatching"),
      },
    },
    gated("ui_edit_destination_list", "G-12", "write", (args, dashboardUrl) =>
      buildEditDestinationListPrompt(
        args as unknown as EditDestinationListParams,
        dashboardUrl,
      ),
    ),
  );

  // =========================================================================
  // G-12: ui_delete_destination_list
  // =========================================================================
  server.registerTool(
    "ui_delete_destination_list",
    {
      title: "Delete Destination List (UI Agent)",
      description:
        "Deletes a destination list via the OCS web dashboard. " +
        "Params: `destination_list_id`. WARNING: This is destructive and cannot be undone." +
        NOTE,
      inputSchema: {
        destination_list_id: z
          .number()
          .describe("ID of the destination list to delete"),
        dry_run: z
          .boolean()
          .optional()
          .describe("Preview the agent prompt without dispatching"),
      },
    },
    gated("ui_delete_destination_list", "G-12", "admin", (args, dashboardUrl) =>
      buildDeleteDestinationListPrompt(
        args as unknown as DeleteDestinationListParams,
        dashboardUrl,
      ),
    ),
  );

  // =========================================================================
  // G-18: ui_delete_package_template
  // =========================================================================
  server.registerTool(
    "ui_delete_package_template",
    {
      title: "Delete Package Template (UI Agent)",
      description:
        "Deletes a package template from the product catalog via the OCS web dashboard. " +
        "This operation is not available via the OCS REST API. " +
        "Params: `template_id`. WARNING: This is destructive." +
        NOTE,
      inputSchema: {
        template_id: z
          .number()
          .describe("ID of the package template to delete"),
        dry_run: z
          .boolean()
          .optional()
          .describe("Preview the agent prompt without dispatching"),
      },
    },
    gated("ui_delete_package_template", "G-18", "admin", (args, dashboardUrl) =>
      buildDeletePackageTemplatePrompt(
        args as unknown as DeletePackageTemplateParams,
        dashboardUrl,
      ),
    ),
  );

  // =========================================================================
  // G-19: ui_edit_location_zone
  // =========================================================================
  server.registerTool(
    "ui_edit_location_zone",
    {
      title: "Edit Location Zone (UI Agent)",
      description:
        "Edits an existing location zone via the OCS web dashboard. " +
        "`create_location_zone` is available via API; edit is UI-only. " +
        "Params: `zone_id`, `new_name`, `add_countries`, `remove_countries`." +
        NOTE,
      inputSchema: {
        zone_id: z.number().describe("ID of the location zone to edit"),
        new_name: z.string().optional().describe("Rename the location zone"),
        add_countries: z
          .array(z.string())
          .optional()
          .describe("ISO country codes to add (e.g. ['NL', 'DE'])"),
        remove_countries: z
          .array(z.string())
          .optional()
          .describe("ISO country codes to remove"),
        dry_run: z
          .boolean()
          .optional()
          .describe("Preview the agent prompt without dispatching"),
      },
    },
    gated("ui_edit_location_zone", "G-19", "write", (args, dashboardUrl) =>
      buildEditLocationZonePrompt(
        args as unknown as EditLocationZoneParams,
        dashboardUrl,
      ),
    ),
  );

  // =========================================================================
  // G-19: ui_delete_location_zone
  // =========================================================================
  server.registerTool(
    "ui_delete_location_zone",
    {
      title: "Delete Location Zone (UI Agent)",
      description:
        "Deletes a location zone via the OCS web dashboard. " +
        "`create_location_zone` is available via API; delete is UI-only. " +
        "Params: `zone_id`. WARNING: Zones in use by active templates may not be deletable." +
        NOTE,
      inputSchema: {
        zone_id: z.number().describe("ID of the location zone to delete"),
        dry_run: z
          .boolean()
          .optional()
          .describe("Preview the agent prompt without dispatching"),
      },
    },
    gated("ui_delete_location_zone", "G-19", "admin", (args, dashboardUrl) =>
      buildDeleteLocationZonePrompt(
        args as unknown as DeleteLocationZoneParams,
        dashboardUrl,
      ),
    ),
  );
}
